import {
  AppHeader,
  AppHeaderBackButton,
  Button,
  ButtonGroup,
  Container,
  Form,
  Typography,
  dayjs,
} from '@ace-clublabs/shared-component-library';
import { AssociateInfo } from '../components/AssociateInfo';
import { useHistory, useLocation } from 'react-router-dom';
import { Pages, SESSION_EXPIRED_STATUS_CODES } from '../utils/constants';
import {
  GenderType,
  GetActiveMemberStatus,
  MSSMemberStatus,
  RoleType,
} from '@ace-clublabs/common-membership-api-types';
import { ClubCode, GenderCode, MSSMembers } from '@lib/types';
import { setGlobalState, useGlobalState } from '../state/state';
import { v4 as uuidv4 } from 'uuid';
import { checkIfMembersMatch } from '../utils/ruleHelper';
import { IFormData } from '../state/model';
import { sources } from '../api/sources';
import { IGetActiveMemberRequest } from '../api/contracts';
import { ActiveMemberAlert } from '../components/ActiveMemberAlert/ActiveMemberAlert';
import { useLayoutEffect, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { PrivacyPolicyDisclaimer } from '../components/Disclaimers';
import { UtagHelper, UtagType } from '../utils/UtagHelper';

interface LocationState {
  roleType: RoleType;
  mockGenderValue?: GenderCode; // this will not be passed in from the MMM page. this is for testing purposes only
  isEditing?: boolean;
  associate?: IEditAssociateData;
}

export interface IEditAssociateData {
  memberName: string; //full display name
  firstName: string;
  lastName: string;
  memberRole: RoleType;
  virtualId: string;
  dateOfBirth: string;
  gender: GenderType;
  status: MSSMemberStatus;
}

interface IAssociateFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: { genderName: string; genderValue: GenderType };
}

export const AssociateForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const associate = location?.state?.associate;
  const editMode = location?.state?.isEditing;
  const [formData] = useGlobalState('formData');
  const [memberDetails] = useGlobalState('memberDetails');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [tags] = useGlobalState('tags');
  const { clubCode, loggedInMember } = memberDetails;
  const [loading, setLoading] = useState(false);
  const [activeMemberCancelTokenSource, setActiveMemberCancelTokenSource] = useState(null as null | CancelTokenSource);
  const checkForActiveMember = !loggedInMember?.secured;
  const [isTagging, setTagging] = useState(true);

  const buttonRef = useRef(null);

  const [isActiveMember, setIsActiveMember] = useState(false);
  const [activeMemberApiError, setActiveMemberApiError] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const { roleType, mockGenderValue } = location.state || {};

  useLayoutEffect(() => {
    UtagHelper.sendTag(UtagType.view, tags, 'enter member info');
    setTagging(false);
  }, []);

  const disableSubmitButton = (disable: boolean) => {
    if (!buttonRef.current) return;

    // @ts-ignore
    buttonRef.current.disabled = disable;
    setSubmitButtonDisabled(() => disable);

    if (disable) {
      // @ts-ignore
      buttonRef.current.setAttribute('tabindex', '-1');
      // @ts-ignore
      buttonRef.current.setAttribute('aria-disabled', 'true');
    } else {
      // @ts-ignore
      buttonRef.current.removeAttribute('tabindex');
      // @ts-ignore
      buttonRef.current.removeAttribute('aria-disabled');
    }
  };

  const submitForm = async (data: IAssociateFormData) => {
    const addedMember = {
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: dayjs(data.dateOfBirth).format('YYYY-MM-DD'),
      gender: data.gender.genderValue,
      roleType: editMode ? associate?.memberRole : roleType,
      status: MSSMemberStatus.ADDED,
      virtualId: editMode ? associate?.virtualId : uuidv4(),
    } as MSSMembers;

    const matchingMember = formData.members?.find((mem) => checkIfMembersMatch(addedMember, mem));
    const isMatchingMemberExisting = !!matchingMember?.customerId;

    let members: MSSMembers[] | undefined;
    if (matchingMember && isMatchingMemberExisting) {
      members = formData.members?.map((mem) =>
        mem.customerId === matchingMember.customerId ? { ...mem, status: MSSMemberStatus.EXISTING } : mem
      );
    } else {
      if (checkForActiveMember) {
        const source = axios.CancelToken.source();
        const cancelToken = source.token;
        setActiveMemberCancelTokenSource(() => source);

        const activeMemberRequest = buildActiveMemberPayload(formData, addedMember);
        setLoading(true);
        disableSubmitButton(true);
        const activeMemberResponse = await sources.getActiveMember(activeMemberRequest, cancelToken);
        setLoading(false);
        disableSubmitButton(false);
        setActiveMemberCancelTokenSource(() => null);

        const { status, data } = activeMemberResponse;
        const success = status === 'success';
        const error = status === 'error';

        if (success && data?.isActiveMember === GetActiveMemberStatus.ACTIVE) {
          setIsActiveMember(true);
          UtagHelper.sendTag(UtagType.link, tags, 'cannot add person', {
            error: 'cannot add person as an associate because already a member',
          });
          return;
        } else if (error) {
          // @ts-ignore
          const timeoutError = SESSION_EXPIRED_STATUS_CODES.includes(activeMemberResponse?.error?.code);
          if (timeoutError) {
            history.push(Pages.TimeoutError);
          }
          setActiveMemberApiError(true);
          return;
        }
      }
      if (editMode) {
        const editingMemberIdx = formData.members?.findIndex((m) => m.virtualId === associate?.virtualId);
        if (editingMemberIdx !== -1) {
          members = [...(formData.members || [])];
          members[editingMemberIdx!] = addedMember;
        }
      } else {
        members = [...(formData.members || []), addedMember];
      }
    }

    setGlobalState('formData', { ...formData, members });
    history.push({
      pathname: Pages.ManageMembershipPage,
      search: UtagHelper.getIntcmpQueryParam(Pages.AssociateForm, editMode ? 'updatemember' : 'addmember'),
    });
  };

  const onCancel = () => {
    if (activeMemberCancelTokenSource) {
      activeMemberCancelTokenSource.cancel('Canceling active member call');
    }

    history.push(Pages.ManageMembershipPage);
  };

  if (isTagging) {
    return <></>;
  }

  return (
    <Container maxWidth="sm">
      <Form onSubmit={submitForm} sx={{ marginBottom: '2.75rem' }}>
        <AppHeader backButton={!isMobileApp && <AppHeaderBackButton />} />
        <Typography variant="h6" sx={{ marginBottom: '1rem' }} className="associate-form-title">
          Tell us about the family member you're adding
        </Typography>
        <ActiveMemberAlert
          visible={isActiveMember || activeMemberApiError}
          variant={isActiveMember ? 'active' : activeMemberApiError ? 'error' : undefined}
        />
        <AssociateInfo
          mockGenderValue={mockGenderValue}
          roleType={roleType}
          loading={loading}
          associate={associate}
          editMode={editMode}
        />
        <ButtonGroup>
          <Button
            id="associateFormAddButton"
            type="submit"
            className={`
              ${loading ? 'loading' : ''} ${submitButtonDisabled ? 'Mui-disabled' : ''} associate-form-add-button`}
            label={editMode ? 'Update member' : 'Add member'}
            fullWidth
            ref={buttonRef}
            loading={loading}
            sx={{
              '&.loading': { backgroundColor: '#1c4da6 !important', borderColor: '#1c4da6 !important' },
            }}
          />
          <Button
            variant="outlined"
            label="Cancel"
            className="associate-form-cancel-button"
            onClick={onCancel}
            sx={{ marginBottom: '1rem' }}
          />
        </ButtonGroup>
        {(clubCode === ClubCode.EC || clubCode === ClubCode.ACMO) && (
          <PrivacyPolicyDisclaimer textVariant="b3Regular" />
        )}
      </Form>
    </Container>
  );
};

const buildActiveMemberPayload = (formData: IFormData, associate: MSSMembers): IGetActiveMemberRequest => {
  const primaryMember = formData?.members?.find((mem) => mem.roleType === RoleType.PRIMARY);
  return {
    memberInfo: {
      firstName: associate?.firstName,
      lastName: associate?.lastName,
    },
    address: primaryMember?.residenceAddress!,
  };
};
